import React, { useEffect, useState } from 'react'
import { Button } from 'reactstrap'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { LoadIndicator } from 'devextreme-react'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { deleteFile } from '../../../../services/endpoints/data'
import { useDispatch } from 'react-redux'
import { dataSlice } from '../../../../redux/slices'
import useAsyncService from '../../../../hooks/useAsyncService'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import { Container, Row, Col } from 'reactstrap'
import { ShowDataGrid } from '../SelectedFilesGrid/ShowDataGrid'
import './DeleteSelectionModal.css'
interface SelectedFile {
  id: string
  displayName: string
  startDate: string
  endDate: string
  extension: string
  vehicle: { name: string, displayName: string }
}
interface DeleteSelectionsModalProps {
  onClose: (deleteCompleted: boolean) => void
}
export const DeleteModal: React.FC<DeleteSelectionsModalProps> = ({
  onClose
}) => {
  const [deleteStarted, setDeleteStarted] = useState(false)
  const [deleteCompleted, setDeleteCompleted] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const versions = useAppSelector((State) => State.data.versions)

  useEffect(() => {
    const selectedFiles = Object.entries(versions).reduce<SelectedFile[]>(
      (acc, [id, version]) => {
        if (version?.selected) {
          acc.push({
            id, // Extract the key as ID
            displayName: version.fileName,
            startDate: version.startDate,
            endDate: version.endDate,
            extension: version.type,
            vehicle: { name: version.vehicleName, displayName: version.vehicleDisplayName }
          })
        }
        return acc
      },
      []
    )
    setSelectedFiles(selectedFiles)
  }, [versions])

  const getBody = () => {
    if (!deleteStarted) {
      return (
        <>
          <_showDeleteWarning selectedFiles={selectedFiles} />
          <ShowDataGrid selectedFiles={selectedFiles} />
        </>
      )
    } else {
      return (
        <_DeleteSpinner
          selectedFiles={selectedFiles}
          onDeleteSuccess={() => setDeleteCompleted(true)}
        />
      )
    }
  }

  const handleClose = () => {
    onClose(deleteCompleted)
  }

  return (
    <RenderDataModal
      onClose={handleClose}
      title="Selected Files"
      className="delete-selection-modal"
      additionalButtons={
        !deleteStarted && selectedFiles.length > 0
          ? [
              <_createDeleteButton
                key="delete-button"
                selectedFiles={selectedFiles}
                setDeleting={setDeleteStarted}
              />,
              <_createClearAllButton
                key="clear-button"
                selectedFiles={selectedFiles}
              />
            ]
          : []
      }
    >
      <Container className="delete-selections-container">
        <Row className="delete-selections-">
          <Col>{getBody()}</Col>
        </Row>
      </Container>
    </RenderDataModal>
  )
}

const _showDeleteWarning: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  return (
    <span
      className={`delete-alert ${
        selectedFiles.length === 0 ? 'alert-warning' : 'alert-danger'
      }`}
    >
      {selectedFiles.length === 0 || !selectedFiles
        ? 'No files selected.'
        : 'Files will be marked end-of-life and can only be recovered by an administrator.'}
    </span>
  )
}

const _DeleteSpinner: React.FC<{
  selectedFiles: SelectedFile[]
  onDeleteSuccess: () => void
}> = ({ selectedFiles, onDeleteSuccess }) => {
  const dispatch = useDispatch()
  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: deleteFilesService,
      delay: 500,
      initialArgs: null
    })

  useEffect(() => {
    if (selectedFiles.length === 0) {
      console.error('Attempting to delete files but no files are selected')
    }
    setServiceArgs(selectedFiles.map((file) => file.id))
  }, [])

  useEffect(() => {
    if (serviceValue) {
      dispatch(dataSlice.actions.clearAllSelections())
      onDeleteSuccess()
    }
  }, [serviceValue])

  if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  }
  if (serviceLoading) {
    return <LoadIndicator />
  }
  if (serviceValue) {
    return (
      <span className={`delete-alert ${'alert-warning'}`}>
        {'All of the requested files have been marked for deletion!'}
      </span>
    )
  }
  return <></>
}

const _createClearAllButton: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  const dispatch = useDispatch()
  const clearAllSelections = () => {
    dispatch(dataSlice.actions.clearAllSelections())
  }

  return (
    <div>
      <Button
        className="modal-btn clear-btn"
        onClick={clearAllSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-ban-circle"></span> Clear
        Selections
      </Button>
    </div>
  )
}

const _createDeleteButton: React.FC<{
  selectedFiles: SelectedFile[]
  setDeleting: (value: boolean) => void
}> = ({ selectedFiles, setDeleting }) => {
  const handleDeleteSelections = () => {
    setDeleting(true)
  }
  return (
    <div>
      <Button
        className="modal-btn delete-btn"
        onClick={handleDeleteSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-bin"></span>
        {'Move to Recycling'}
      </Button>
    </div>
  )
}

const deleteFilesService = async (fileIds: string[]) => {
  try {
    const results = await Promise.all(
      fileIds.map((fileId) => deleteFile(Number(fileId)))
    )
    return results
  } catch (err) {
    throw new Error('One or more files failed to delete')
  }
}
